import type { Language } from "@/i18n";

export function measurementJourneyCopy(
  locale: Language,
  measurementTags: readonly string[],
) {
  const journey: Record<
    Language,
    readonly [
      { label: string; value: string; change: string },
      { label: string; value: string; change: string },
      { label: string; value: string; change: string },
    ]
  > = {
    fi: [
      {
        label: "Lähtökohta",
        value: "Julkinen metsävaratieto",
        change: "Saat ensimmäisen kuvan maksutta",
      },
      {
        label: "Täydennä maastotiedoilla",
        value: "Omat mittaukset, esimerkiksi Arboreal Forestilla",
        change: measurementTags[0],
      },
      {
        label: "Näe vaikutus",
        value: "Tarkemmat lähtötiedot arviolle ja suunnitelmalle",
        change: measurementTags[1],
      },
    ],
    en: [
      {
        label: "Starting point",
        value: "Public forest inventory",
        change: "Get the first picture for free",
      },
      {
        label: "Add from the field",
        value: "Your measurements, for example with Arboreal Forest",
        change: measurementTags[0],
      },
      {
        label: "See the benefit",
        value: "A sharper valuation and plan",
        change: measurementTags[1],
      },
    ],
    sv: [
      {
        label: "Utgångspunkt",
        value: "Offentlig skogsinventering",
        change: "Få den första bilden gratis",
      },
      {
        label: "Lägg till från fältet",
        value: "Egna mätningar, till exempel med Arboreal Forest",
        change: measurementTags[0],
      },
      {
        label: "Se nyttan",
        value: "En skarpare värdering och plan",
        change: measurementTags[1],
      },
    ],
    es: [
      {
        label: "Punto de partida",
        value: "Inventario forestal público",
        change: "Obtén la primera imagen gratis",
      },
      {
        label: "Añade datos de campo",
        value: "Tus mediciones, por ejemplo con Arboreal Forest",
        change: measurementTags[0],
      },
      {
        label: "Comprueba el beneficio",
        value: "Valoración y plan más precisos",
        change: measurementTags[1],
      },
    ],
  };

  return journey[locale];
}

export function measurementProofCopy(locale: Language) {
  const localized: Record<
    Language,
    {
      title: string;
      explanation: string;
      caption: string;
      publicLabel: string;
      measuredLabel: string;
      observationLabel: string;
      selectionLabel: string;
      volumeLabel: string;
      publicBenefit: string;
      measuredBenefit: string;
      publicSource: string;
      measuredSource: string;
    }
  > = {
    fi: {
      title: "Näe, mitä oma mittauksesi muuttaa",
      explanation:
        "Sama kuvio näkyy julkisen metsävaratiedon ja puhelinskannauksen luvuilla rinnakkain.",
      caption:
        "Esimerkkitilanne 5.9.2026: Lehti AI näyttää lähtöarvot, mittausarvot ja niiden välisen eron.",
      publicLabel: "Julkinen tilavuus",
      measuredLabel: "Puhelinskannaus",
      observationLabel: "Havaintopäivä",
      selectionLabel: "Vertaa tietopohjaa",
      volumeLabel: "Puuston tilavuus",
      publicBenefit:
        "Näet maksuttoman lähtökuvan ja päätöksestä vielä puuttuvat tiedot.",
      measuredBenefit:
        "Puhelinmittaus näyttää puuston rakennetta, jota yksi keskiluku ei kuvaa. Näet, miten uusi havainto muuttaa saman kuvion lähtötietoja.",
      publicSource: "Metsäkeskuksen julkinen metsävaratieto",
      measuredSource: "Omistajan lisäämä puhelinmittaus",
    },
    en: {
      title: "See exactly what your measurements changed",
      explanation:
        "The same stand keeps the public inventory and phone-scan values side by side.",
      caption:
        "Fixed example captured on 5 September 2026: Lehti AI shows the served baseline, measured values, and their difference.",
      publicLabel: "Public volume",
      measuredLabel: "Phone scan",
      observationLabel: "Observation date",
      selectionLabel: "Compare the evidence basis",
      volumeLabel: "Standing volume",
      publicBenefit:
        "See the free starting picture and what evidence is still missing for the decision.",
      measuredBenefit:
        "Phone measurements reveal forest structure that one average cannot show. See how the new observation changes the evidence for the same stand.",
      publicSource: "Finnish Forest Centre public inventory",
      measuredSource: "Owner-provided phone measurement",
    },
    sv: {
      title: "Se exakt vad din mätning förändrade",
      explanation:
        "Samma bestånd visar offentlig skogsdata och mobilskanningens värden sida vid sida.",
      caption:
        "Fast exempel från 5 september 2026: Lehti AI visar levererade utgångsvärden, mätvärden och skillnaden.",
      publicLabel: "Offentlig volym",
      measuredLabel: "Mobilskanning",
      observationLabel: "Observationsdatum",
      selectionLabel: "Jämför underlaget",
      volumeLabel: "Virkesvolym",
      publicBenefit:
        "Se den kostnadsfria utgångsbilden och vilket underlag som ännu saknas för beslutet.",
      measuredBenefit:
        "Mobilmätning visar skogsstruktur som ett enda medelvärde inte fångar. Se hur den nya observationen ändrar underlaget för samma bestånd.",
      publicSource: "Skogscentralens offentliga skogsdata",
      measuredSource: "Mobilmätning som ägaren har lagt till",
    },
    es: {
      title: "Mira exactamente qué cambió con tus mediciones",
      explanation:
        "El mismo rodal mantiene juntos los valores del inventario público y del escaneo móvil.",
      caption:
        "Ejemplo fijo capturado el 5 de septiembre de 2026: Lehti AI muestra el punto de partida, la medición y su diferencia.",
      publicLabel: "Volumen público",
      measuredLabel: "Escaneo móvil",
      observationLabel: "Fecha de observación",
      selectionLabel: "Compara la base de datos",
      volumeLabel: "Volumen en pie",
      publicBenefit:
        "Consulta el punto de partida gratuito y qué datos siguen faltando para decidir.",
      measuredBenefit:
        "La medición con móvil revela una estructura que un solo promedio no muestra. Comprueba cómo la nueva observación cambia la base del mismo rodal.",
      publicSource: "Inventario público del Centro Forestal de Finlandia",
      measuredSource: "Medición con móvil aportada por el propietario",
    },
  };

  return localized[locale];
}

export function commercialLadderCopy(locale: Language) {
  const localized: Record<
    Language,
    {
      title: string;
      answer: string;
      steps: readonly [
        { label: string; explanation: string; status: string },
        { label: string; explanation: string; status: string },
        { label: string; explanation: string; status: string },
      ];
    }
  > = {
    fi: {
      title: "Aloita maksutta. Saat tarjousten vertailusta vuoden päätöstuen.",
      answer:
        "Kartta, kuviot ja hinta-arvion lähtökohdat ovat maksuttomia. Tarjousten purku sisältää vuoden Tonttipaketin samalle metsälle, tai voit hankkia vuosipaketin suoraan.",
      steps: [
        {
          label: "Lisää metsä maksutta",
          explanation:
            "Näe kartta, kuviot, lähteet ja hinta-arvion lähtökohdat.",
          status: "0 €",
        },
        {
          label: "Pura tarjoukset tarvittaessa",
          explanation:
            "Vertaa tarjoukset euroina ja näe, mistä niiden ero syntyy.",
          status: "Kertamaksu",
        },
        {
          label: "Jatka vuoden päätöstuella",
          explanation:
            "Tarjousten purku avaa samalla vuodeksi keskusteluavun, suunnittelun ja Metsävahdin tälle metsälle.",
          status: "Sisältyy Tarjousten purkuun",
        },
      ],
    },
    en: {
      title: "Start free. Offer Decode brings a year of decision support.",
      answer:
        "The map, stands, and valuation basis are free. Offer Decode includes one year of Plot Package for the same forest, or you can buy the annual package directly.",
      steps: [
        {
          label: "Add a forest for free",
          explanation:
            "See the map, stands, sources, and valuation starting point.",
          status: "€0",
        },
        {
          label: "Decode offers when needed",
          explanation:
            "Compare offers in euros and see what causes the difference.",
          status: "One-off",
        },
        {
          label: "Continue with a year of decision support",
          explanation:
            "Offer Decode also opens a year of conversation help, planning, and Forest Sentry for this forest.",
          status: "Included with Offer Decode",
        },
      ],
    },
    sv: {
      title: "Börja gratis. Anbudstolkningen ger ett års beslutsstöd.",
      answer:
        "Kartan, bestånden och värderingsunderlaget är gratis. Anbudstolkningen inkluderar ett år med Plot Package för samma skog, eller så kan du köpa årspaketet direkt.",
      steps: [
        {
          label: "Lägg till en skog gratis",
          explanation:
            "Se kartan, bestånden, källorna och värderingens utgångspunkt.",
          status: "0 €",
        },
        {
          label: "Tolka erbjudanden vid behov",
          explanation:
            "Jämför erbjudanden i euro och se vad skillnaden beror på.",
          status: "Engångsköp",
        },
        {
          label: "Fortsätt med ett års beslutsstöd",
          explanation:
            "Anbudstolkningen öppnar samtidigt ett år med samtalshjälp, planering och Skogsvakt för denna skog.",
          status: "Ingår i anbudstolkningen",
        },
      ],
    },
    es: {
      title: "Empieza gratis. El análisis de ofertas incluye un año de apoyo.",
      answer:
        "El mapa, los rodales y la base de la valoración son gratuitos. El análisis de ofertas incluye un año de Paquete forestal para el mismo bosque, o puedes contratar directamente el paquete anual.",
      steps: [
        {
          label: "Añade un bosque gratis",
          explanation:
            "Consulta el mapa, los rodales, las fuentes y la base de la valoración.",
          status: "0 €",
        },
        {
          label: "Descifra ofertas cuando lo necesites",
          explanation:
            "Compara las ofertas en euros y descubre de dónde surge la diferencia.",
          status: "Pago único",
        },
        {
          label: "Continúa con un año de apoyo para decidir",
          explanation:
            "El análisis de ofertas abre además un año de ayuda conversacional, planificación y vigilancia para este bosque.",
          status: "Incluido con el análisis de ofertas",
        },
      ],
    },
  };

  return localized[locale];
}

export function finalOwnerCloseCopy(locale: Language) {
  const localized: Record<
    Language,
    {
      title: string;
      answer: string;
      context: string;
      free: string;
      offers: string;
      package: string;
    }
  > = {
    fi: {
      title: "Näe ensin, mitä omassa metsässäsi kannattaa tehdä.",
      answer: "Aloita maksutta omasta metsästäsi ja sen tietopohjasta.",
      context:
        "Valitse maksullinen apu vasta, kun haluat vertailla tarjouksia tai pitää metsäsi suunnitelman ja seurannan ajan tasalla.",
      free: "Lisää metsä maksutta",
      offers: "Vertaa omat tarjoukseni",
      package: "Tutustu Tonttipakettiin",
    },
    en: {
      title: "First see what is worth doing in your own forest.",
      answer: "Start free with your own forest and its evidence base.",
      context:
        "Choose paid help only when you want to compare offers or keep your plan and monitoring current.",
      free: "Add a forest for free",
      offers: "Compare my offers",
      package: "See annual decision support",
    },
    sv: {
      title: "Se först vad som är värt att göra i din egen skog.",
      answer: "Börja gratis med din egen skog och dess kunskapsunderlag.",
      context:
        "Välj betald hjälp först när du vill jämföra anbud eller hålla planen och uppföljningen aktuell.",
      free: "Lägg till skogen gratis",
      offers: "Jämför mina anbud",
      package: "Se årets beslutsstöd",
    },
    es: {
      title: "Descubre primero qué merece la pena hacer en tu bosque.",
      answer: "Empieza gratis con tu bosque y la base de datos disponible.",
      context:
        "Elige ayuda de pago solo cuando quieras comparar ofertas o mantener al día el plan y el seguimiento.",
      free: "Añade el bosque gratis",
      offers: "Comparar mis ofertas",
      package: "Ver el apoyo anual",
    },
  };

  return localized[locale];
}
